import {
  asapScheduler,
  distinctUntilChanged,
  MonoTypeOperatorFunction,
  pipe,
  Subscription,
  tap,
} from 'rxjs';
import { Observable } from 'rxjs';
import {
  APP_BOOTSTRAP_LISTENER,
  InjectionToken,
  Inject,
  Type,
} from '@angular/core';
import { ActionCreator, Store, Action } from '@ngrx/store';
import { EffectsModule, EffectSources, provideEffects } from '@ngrx/effects';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { formatDate } from '@angular/common';
import {
  EntityCompleteness,
  EntityDtoOfGuid,
} from '@core/services/api-client.generated';
import * as _ from 'lodash-es';

/**
 * This function coerces a string into a string literal type.
 * Using tagged union types in TypeScript 2.0, this enables
 * powerful typechecking of our reducers.
 *
 * Since every action label passes through this function it
 * is a good place to ensure all of our action labels
 * are unique.
 */

const typeCache: { [label: string]: boolean } = {};

export function type<T>(label: T | ''): T {
  if (typeCache[<string>label]) {
    throw new Error(`Action type "${label}" is not unqiue"`);
  }

  typeCache[<string>label] = true;

  return <T>label;
}

export type WithOptional<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>>;
export type Overwrite<T1, T2> = {
  [P in Exclude<keyof T1, keyof T2>]: T1[P];
} & T2;

export const LOADING: LOADING = 'LOADING';
export type LOADING = 'LOADING';

export const deepDistinctUntilChanged = <T>(observable$: Observable<T>) =>
  observable$.pipe(distinctUntilChanged<T>((a, b) => _.isEqual(a, b)));

export function getRandomItem<T>(array: T[]): T | null {
  if (array.length === 0) return null;
  const randomIndex = getRandomInt(0, array.length - 1);
  return array[randomIndex];
}

export function getRandomInt(min, max) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
}

export const BOOTSTRAP_EFFECTS = new InjectionToken('Bootstrap Effects');

export function bootstrapEffects(effects: Type<any>[], sources: EffectSources) {
  return () => {
    effects.forEach((effect) => sources.addEffects(effect));
  };
}

export function createInstances(...instances: any[]) {
  return instances;
}

export function provideBootstrapEffects(effects: Type<any>[]) {
  return [
    provideEffects(),
    effects,
    { provide: BOOTSTRAP_EFFECTS, deps: effects, useFactory: createInstances },
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: bootstrapEffects,
      deps: [[new Inject(BOOTSTRAP_EFFECTS)], EffectSources],
    },
  ];
}

export namespace Loading {
  export enum Status {
    error,
    loading,
    success,
  }

  interface BaseState {
    status: Status;
  }

  interface ErrorState<TError = string> extends BaseState {
    status: Status.error;
    error: TError;
  }

  export const errorState = <TError>(error: TError): ErrorState<TError> => ({
    status: Status.error,
    error,
  });

  interface LoadingState extends BaseState {
    status: Status.loading;
  }

  export const loadingState = (): LoadingState => ({
    status: Status.loading,
  });

  interface SuccessState<TValue> extends BaseState {
    status: Status.success;
    value: TValue;
  }

  export const successState = <TValue>(
    value: TValue
  ): SuccessState<TValue> => ({
    status: Status.success,
    value,
  });

  export type State<TValue, TError = string> =
    | ErrorState<TError>
    | LoadingState
    | SuccessState<TValue>;
}

export type AngularForm<T> = T extends (infer ElementType)[]
  ? FormArray<AngularForm<ElementType>>
  : T extends Date
    ? FormControl<T>
    : T extends object
      ? FormGroup<{
          [Key in keyof T]: AngularForm<T[Key]>;
        }>
      : FormControl<T>;

export type SimpleNonNullableFormGroup<T extends Record<string, any>> =
  FormGroup<{
    [K in keyof T]: FormControl<T[K]>;
  }>;

export const emailArrayValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  let isPassed = true;

  if (Array.isArray(control.value)) {
    for (const email of control.value) {
      const innerControl: FormControl = new FormControl(
        email,
        Validators.email
      );
      if (innerControl.errors && innerControl.errors.email) {
        isPassed = false;
        break;
      }
    }
  } else {
    isPassed = false;
  }

  return isPassed ? null : { emailArray: true };
};

export const requiredArrayValidator: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const isPassed: boolean =
    Array.isArray(control.value) && control.value.length > 0;
  return isPassed ? null : { required: true };
};

export const EMAIL_REGEXP =
  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

interface WithPriority {
  priority?: number;
}

/** HOF to sort the breakpoints by descending priority */
export function sortDescendingPriority<T extends WithPriority>(
  a: T | null,
  b: T | null
): number {
  const priorityA = a ? a.priority || 0 : 0;
  const priorityB = b ? b.priority || 0 : 0;
  return priorityB - priorityA;
}

/** HOF to sort the breakpoints by ascending priority */
export function sortAscendingPriority<T extends WithPriority>(
  a: T,
  b: T
): number {
  const pA = a.priority || 0;
  const pB = b.priority || 0;
  return pA - pB;
}

export const throwErrorExp = (error: any): never => {
  throw new Error(error);
};
export const breakpoint = <TKind>(value: TKind): TKind => {
  // eslint-disable-next-line no-debugger
  debugger;
  return value;
};

type Replacement<M extends [any, any], T> = M extends any
  ? [T] extends [M[0]]
    ? M[1]
    : never
  : never;

export type DeepReplace<T, M extends [any, any]> = {
  [P in keyof T]: T[P] extends M[0]
    ? Replacement<M, T[P]>
    : T[P] extends object
      ? DeepReplace<T[P], M>
      : T[P];
};

export const ternary = <TInput, T>(
  value: TInput,
  trueValue: (value: TInput) => T,
  falseValue: (value: TInput) => T | void = () => {}
) => (value ? trueValue(value) : falseValue(value));

export const filteredTap = <TInput, TTapped extends TInput>(
  predicate: (value: TInput) => value is TTapped,
  tapFn: (value: TTapped) => void
) =>
  tap((value: TInput) => {
    if (predicate(value)) {
      tapFn(value);
    }
  });

const dateFormat = 'd MMMM y';
const timeFormat = 'h:mm a (z)';
export const formatDateTime = (date: Date | string | number) =>
  `${formatDate(date, dateFormat, 'en-GB')} at ${formatDate(
    date,
    timeFormat,
    'en-GB'
  )}`;

export function debounceSync<T>(): MonoTypeOperatorFunction<T> {
  return (source) =>
    new Observable<T>((observer) => {
      let actionSubscription: Subscription | undefined;
      let actionValue: T;
      let hasValue = false;
      const rootSubscription = new Subscription();
      rootSubscription.add(
        source.subscribe({
          complete: () => {
            if (actionSubscription && hasValue) {
              observer.next(actionValue);
            }
            if (actionSubscription) {
              actionSubscription.unsubscribe();
              actionSubscription = undefined;
            }
            hasValue = false;
            observer.complete();
          },
          error: (error) => {
            if (actionSubscription) {
              actionSubscription.unsubscribe();
              actionSubscription = undefined;
            }
            hasValue = false;
            observer.error(error);
          },
          next: (value) => {
            actionValue = value;
            hasValue = true;
            if (!actionSubscription) {
              actionSubscription = asapScheduler.schedule(() => {
                if (hasValue) {
                  observer.next(actionValue);
                }
                actionSubscription = undefined;
              });
              rootSubscription.add(actionSubscription);
            }
          },
        })
      );
      return rootSubscription;
    });
}

export type ReplaceValuesWith<T, ValueType> = {
  [K in keyof T]: ValueType;
};
type NullOrUndefined = null | undefined;

const isNotNullOrUndefined = <T>(
  value: T
): value is Exclude<T, null | undefined> =>
  value !== null && value !== undefined;
export const applyIfNonNullable = <T, U>(
  value: T,
  transformer: (value: NonNullable<T>) => U
): U | (T & NullOrUndefined) =>
  isNotNullOrUndefined(value)
    ? transformer(value as NonNullable<T>)
    : (value as T & NullOrUndefined);

// export const mapFn = <T, U>(value: T, transformer: (value: T) => U): U =>
//   transformer(value);
type Transformer<Args extends any[], R> = (...args: Args) => R;

export function mapFn<T1, R1>(initialValue: T1, t1: Transformer<[T1], R1>): R1;

export function mapFn<T1, R1, R2>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>
): R2;

export function mapFn<T1, R1, R2, R3>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>
): R3;

export function mapFn<T1, R1, R2, R3, R4>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>
): R4;

export function mapFn<T1, R1, R2, R3, R4, R5>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>
): R5;

export function mapFn<T1, R1, R2, R3, R4, R5, R6>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>
): R6;

export function mapFn<T1, R1, R2, R3, R4, R5, R6, R7>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>,
  t7: Transformer<[T1, R1, R2, R3, R4, R5, R6], R7>
): R7;

export function mapFn<T1, R1, R2, R3, R4, R5, R6, R7, R8>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>,
  t7: Transformer<[T1, R1, R2, R3, R4, R5, R6], R7>,
  t8: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7], R8>
): R8;

export function mapFn<T1, R1, R2, R3, R4, R5, R6, R7, R8, R9>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>,
  t7: Transformer<[T1, R1, R2, R3, R4, R5, R6], R7>,
  t8: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7], R8>,
  t9: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8], R9>
): R9;

export function mapFn<T1, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>,
  t7: Transformer<[T1, R1, R2, R3, R4, R5, R6], R7>,
  t8: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7], R8>,
  t9: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8], R9>,
  t10: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8, R9], R10>
): R10;

export function mapFn<T1, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>,
  t7: Transformer<[T1, R1, R2, R3, R4, R5, R6], R7>,
  t8: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7], R8>,
  t9: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8], R9>,
  t10: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8, R9], R10>,
  t11: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10], R11>
): R11;

export function mapFn<T1, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11, R12>(
  initialValue: T1,
  t1: Transformer<[T1], R1>,
  t2: Transformer<[T1, R1], R2>,
  t3: Transformer<[T1, R1, R2], R3>,
  t4: Transformer<[T1, R1, R2, R3], R4>,
  t5: Transformer<[T1, R1, R2, R3, R4], R5>,
  t6: Transformer<[T1, R1, R2, R3, R4, R5], R6>,
  t7: Transformer<[T1, R1, R2, R3, R4, R5, R6], R7>,
  t8: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7], R8>,
  t9: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8], R9>,
  t10: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8, R9], R10>,
  t11: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10], R11>,
  t12: Transformer<[T1, R1, R2, R3, R4, R5, R6, R7, R8, R9, R10, R11], R12>
): R12;

// General implementation
export function mapFn(
  initialValue: any,
  ...transformers: Transformer<any, any>[]
): any {
  const values: any[] = [initialValue];

  const applyTransformer = <Args extends any[], R>(
    transformer: Transformer<Args, R>,
    args: Args
  ): R => transformer(...args);

  for (const transformer of transformers) {
    const result = applyTransformer(transformer, values);
    values.push(result);
  }

  return values[values.length - 1];
}

export type KeyMap<TFormModel extends object> = {
  [key in keyof TFormModel]: key;
};
export const createKeyMap = <TFormModel extends object>(
  formModel: TFormModel
): KeyMap<TFormModel> => {
  const entries = Object.entries(formModel);
  const map = entries.reduce((acc, [key, value]) => {
    acc[key] = key;
    return acc;
  }, {} as KeyMap<TFormModel>);
  return map;
};
export type ReplacePropertyType<
  T extends object,
  TPropertyKey extends keyof T,
  TNewPropertyType,
> = Omit<T, TPropertyKey> & {
  [K in TPropertyKey]: TNewPropertyType;
};
export type SimpleActionCreator<
  TActionType extends string,
  TActionProps extends object & { [key: string]: any },
> = ActionCreator<
  TActionType,
  (props: TActionProps) => TActionProps & Action<TActionType>
>;
export function mapValues<T, U>(
  dict: { [key: string]: unknown & T },
  mapFn: (value: T, key: string) => U
): { [key: string]: U } {
  return Object.keys(dict).reduce(
    (acc, key) => ({
      ...acc,
      [key]: mapFn(dict[key], key),
    }),
    {} as { [key: string]: U }
  );
}

export type DifferenceInfo = {
  path: string;
  obj1: {} | undefined;
  obj2: {} | undefined;
};
export type ComparisionInfo = {
  differences: DifferenceInfo[];
  jsonCompareEqual: boolean;
  referenceEqual: boolean;
  deepEqual: boolean;
};

export const findDifferences = (obj1, obj2, path = ''): DifferenceInfo[] => {
  let differences: DifferenceInfo[] = [];

  if (obj1 === obj2) {
    return differences;
  }

  if (
    obj1 == null ||
    typeof obj1 !== 'object' ||
    obj2 == null ||
    typeof obj2 !== 'object'
  ) {
    differences.push({ path: path || 'root', obj1, obj2 });
    return differences;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  for (let key of keys1) {
    const newPath = path ? `${path}.${key}` : key;
    if (!keys2.includes(key)) {
      differences.push({ path: newPath, obj1: obj1[key], obj2: undefined });
    } else {
      differences = differences.concat(
        findDifferences(obj1[key], obj2[key], newPath)
      );
    }
  }
  for (let key of keys2) {
    if (!keys1.includes(key)) {
      const newPath = path ? `${path}.${key}` : key;
      differences.push({ path: newPath, obj1: undefined, obj2: obj2[key] });
    }
  }

  return differences;
};
export const OptionStatus = {
  removed: 'removed',
  added: 'added',
  unchanged: 'unchanged',
} as const;
export type OptionStatus = (typeof OptionStatus)[keyof typeof OptionStatus];
export const diffArrays = (new1: string[], old1: string[]) =>
    new1.reduce(
      (acc, currNew, index) =>
        mapFn(
          old1[index],
          () => acc.map((o) => o.value),
          (currOld, accVals) => [
            ...acc,
            ...(accVals.includes(currNew)
              ? []
              : [
                  {
                    value: currNew,
                    status: old1.includes(currNew)
                      ? OptionStatus.unchanged
                      : OptionStatus.added,
                  },
                ]),
            ...(currOld === undefined || new1.includes(currOld)
              ? []
              : [
                  {
                    value: currOld,
                    status: OptionStatus.removed,
                  },
                ]),
            ...(index === new1.length - 1
              ? old1
                  .slice(new1.length)
                  .filter((oi) => ![...accVals, currNew].includes(oi))
                  .map((rv) => ({
                    value: rv,
                    status: OptionStatus.removed,
                  }))
              : []),
          ]
        ),
      [] as { value: string; status: OptionStatus }[]
    );

export type IsNullable<T> = null extends T ? true : false;
export type NonNullable<T> = T extends null ? never : T;
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type Replace<T, K> = Omit<T, keyof K> & K;
export type NullSafeOptional<T, K extends keyof NonNullable<T>> = null extends T
  ? Optional<NonNullable<T>, K> | null
  : Optional<NonNullable<T>, K>;

export const arrayToDictionary = <
  TInput,
  TId extends PropertyKey,
  TValue = TInput,
>(
  array: TInput[],
  idFn: (item: TInput) => TId,
  valueFn: (item: TInput) => TValue = (item: TInput) => item as unknown as TValue
): Record<PropertyKey, TValue> => {
  const keyValues = array.map((item) => [idFn(item), valueFn(item)] as const);
  return Object.fromEntries(keyValues);
};
